/**
 * Compact inline goal editor — opened from the dashboard `GoalsPanel` sidebar.
 * Scoped to a single office (via `officeId` prop) and uses the dashboard's
 * measures registry from context.
 *
 * Role (Phase B10): the dashboard-side goal editor. Pair with
 * `GoalEditorFull.tsx` (canonical full-page modal used on /exec/goals) and
 * `GoalsPanel.tsx` (the dashboard sidebar that hosts this editor).
 */
import { useState, useMemo } from 'react'
import { X, Target } from 'lucide-react'
import { useExecDashboard } from '../../contexts/ExecDashboardContext'
import { execAPI } from '../../services/exec'
import type { Goal, GoalTargetValue } from '../../types/exec'

interface Props {
  open: boolean
  onClose: () => void
  /** Pre-selected office ID (from dashboard scope or user's office) */
  officeId: number | null
  /** Existing goal to edit, or null to create a new one */
  existingGoal?: Goal | null
}

export default function GoalEditor({ open, onClose, officeId, existingGoal }: Props) {
  const { measures, refetchGoals } = useExecDashboard()

  // Only measures with a kpi_field can have goals
  const goalableMeasures = useMemo(
    () =>
      Object.entries(measures)
        .filter(([_, def]) => def.kpi_field)
        .map(([key, def]) => ({ key, ...def })),
    [measures],
  )

  const [measure, setMeasure] = useState(existingGoal?.measure ?? '')
  const [targetValue, setTargetValue] = useState<string>(
    existingGoal?.target_value?.value?.toString() ?? '',
  )
  const [description, setDescription] = useState(existingGoal?.description ?? '')
  const [saving, setSaving] = useState(false)
  const [error, setError] = useState<string | null>(null)

  // Derive direction and unit from the selected measure
  const selectedMeasure = measures[measure]
  const direction = selectedMeasure?.kpi_direction ?? 'lower_is_better'
  const unit = selectedMeasure?.kpi_format ?? 'number'

  const handleSave = async () => {
    if (!officeId || !measure || !targetValue) return

    setSaving(true)
    setError(null)

    const payload: GoalTargetValue = {
      value: parseFloat(targetValue),
      direction,
      unit,
    }

    try {
      if (existingGoal) {
        await execAPI.updateGoal(existingGoal.id, {
          target_value: payload,
          description,
        })
      } else {
        await execAPI.createGoal({
          name: '',
          office: officeId,
          portfolio_group: null,
          measure,
          target_value: payload,
          benchmark_sources: [],
          description,
          effective_from: null,
          effective_to: null,
        })
      }
      await refetchGoals()
      onClose()
    } catch (err: any) {
      setError(err.message || 'Failed to save goal')
    } finally {
      setSaving(false)
    }
  }

  if (!open) return null

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40">
      <div className="bg-white dark:bg-gray-900 rounded-xl shadow-xl w-full max-w-md">
        {/* Header */}
        <div className="flex items-center justify-between px-4 py-3 border-b border-gray-200 dark:border-gray-700">
          <div className="flex items-center gap-2">
            <Target size={18} className="text-blue-500" />
            <h2 className="text-base font-semibold text-gray-900 dark:text-gray-100">
              {existingGoal ? 'Edit Goal' : 'Set Goal'}
            </h2>
          </div>
          <button onClick={onClose} className="p-1 rounded hover:bg-gray-100 dark:hover:bg-gray-800">
            <X size={18} />
          </button>
        </div>

        {/* Body */}
        <div className="px-4 py-4 space-y-4">
          {/* Measure selector */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Metric
            </label>
            <select
              value={measure}
              onChange={(e) => setMeasure(e.target.value)}
              disabled={!!existingGoal}
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 disabled:opacity-50"
            >
              <option value="">Select a metric...</option>
              {goalableMeasures.map((m) => (
                <option key={m.key} value={m.key}>
                  {m.label}
                </option>
              ))}
            </select>
          </div>

          {/* Target value */}
          {measure && selectedMeasure && (
            <div>
              <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                Target
                {direction === 'lower_is_better' && (
                  <span className="text-xs text-gray-400 ml-1">(lower is better)</span>
                )}
                {direction === 'higher_is_better' && (
                  <span className="text-xs text-gray-400 ml-1">(higher is better)</span>
                )}
              </label>
              <div className="flex items-center gap-2">
                <span className="text-sm text-gray-500">
                  {direction === 'lower_is_better' ? '≤' : '≥'}
                </span>
                <input
                  type="number"
                  value={targetValue}
                  onChange={(e) => setTargetValue(e.target.value)}
                  placeholder="e.g. 30"
                  className="flex-1 px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100"
                />
                <span className="text-sm text-gray-500">
                  {unit === 'days' && 'days'}
                  {unit === 'percent' && '%'}
                  {unit === 'dollars' && '$'}
                </span>
              </div>
            </div>
          )}

          {/* Description */}
          <div>
            <label className="block text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
              Description
              <span className="text-xs text-gray-400 ml-1">(optional)</span>
            </label>
            <textarea
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              rows={2}
              placeholder="e.g. Reduce median contract award time to 30 days by FY2027"
              className="w-full px-3 py-2 text-sm rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 text-gray-900 dark:text-gray-100 resize-none"
            />
          </div>

          {error && (
            <p className="text-sm text-red-500">{error}</p>
          )}
        </div>

        {/* Footer */}
        <div className="flex items-center justify-end gap-2 px-4 py-3 border-t border-gray-100 dark:border-gray-800">
          <button
            onClick={onClose}
            className="px-3 py-1.5 text-sm rounded-lg text-gray-600 hover:bg-gray-100 dark:hover:bg-gray-800"
          >
            Cancel
          </button>
          <button
            onClick={handleSave}
            disabled={saving || !measure || !targetValue || !officeId}
            className="px-4 py-1.5 text-sm font-medium rounded-lg bg-blue-600 text-white hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            {saving ? 'Saving...' : existingGoal ? 'Update Goal' : 'Set Goal'}
          </button>
        </div>
      </div>
    </div>
  )
}
